import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    useLocation,
    useNavigate,
} from "react-router-dom";
import Swal from "sweetalert2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import {
    FaArrowLeft,
    FaCheckCircle,
    FaDownload,
    FaFileInvoiceDollar,
    FaLock,
    FaPhoneAlt,
    FaPrint,
    FaReceipt,
    FaShieldAlt,
    FaUserGraduate,
} from "react-icons/fa";

import api from "../api/api";
import brandLogo from "./passionaAidsLogo.jpg";

const BRAND = {
    navy: "#062A4B",
    blue: "#063A6B",
    gold: "#F6C944",
    orange: "#C0750D",
    green: "#4FAE22",
    red: "#C62828",
    text: "#172033",
    muted: "#687386",
    border: "#DCE4EE",
    background: "#F3F6FA",
    white: "#FFFFFF",
};

const APPLICATION_KEY =
    "paf_entrance_application";

const RECEIPT_KEY =
    "paf_entrance_receipt";

const APPLICATION_NUMBER_KEY =
    "paf_entrance_application_number";

const STATUS_ENDPOINT =
    "/api/entrance-applications/payment/status/";

const REGISTRATION_ROUTE =
    "/entranceRegistration";

const ORGANIZATION = {
    name: "PASSION AIDS THE FUTURE",

    shortName:
        "PASSION AIDS THE FUTURE",

    motto:
        "Team Work Makes the Dreams Work",

    address:
        "Zubah Town Taxi Turning Point, Rehab Drive Community, Paynesville City, Montserrado County, Liberia",

    phones: [
        "077 384 5837",
        "077 802 6149",
        "088 155 8103",
    ],
};

const safeText = (
    value,
    fallback = "—"
) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return fallback;
    }

    if (
        typeof value === "string" ||
        typeof value === "number"
    ) {
        return String(value);
    }

    if (typeof value === "object") {
        return (
            safeText(
                value.name,
                ""
            ) ||
            safeText(
                value.label,
                ""
            ) ||
            safeText(
                value.title,
                ""
            ) ||
            safeText(
                value.display_name,
                ""
            ) ||
            safeText(
                value.academic_year,
                ""
            ) ||
            safeText(
                value.year,
                ""
            ) ||
            safeText(
                value.value,
                ""
            ) ||
            fallback
        );
    }

    return fallback;
};

const readJSON = (key) => {
    try {
        const value =
            localStorage.getItem(key);

        if (!value) {
            return null;
        }

        return JSON.parse(value);
    } catch (error) {
        console.error(
            `Unable to read ${key}:`,
            error
        );

        return null;
    }
};

const normalizeApplicationNumber = (
    value
) =>
    String(value || "")
        .trim()
        .toUpperCase();

const getApplicantName = (
    application
) => {
    if (!application) {
        return "Applicant";
    }

    if (
        application.applicant_name
    ) {
        return safeText(
            application.applicant_name,
            "Applicant"
        );
    }

    const name = [
        application.first_name,
        application.middle_name,
        application.last_name,
    ]
        .map((item) =>
            safeText(item, "")
        )
        .filter(Boolean)
        .join(" ")
        .trim();

    return name || "Applicant";
};

const formatMoney = (
    amount,
    currency = "LRD"
) => {
    const numericAmount =
        Number(amount);

    if (
        !Number.isFinite(
            numericAmount
        )
    ) {
        return `${currency} 0.00`;
    }

    return `${currency} ${numericAmount.toLocaleString(
        "en-US",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    )}`;
};

const formatDate = (
    value,
    fallback = "—"
) => {
    if (!value) {
        return fallback;
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return fallback;
    }

    return date.toLocaleString(
        "en-US",
        {
            year: "numeric",
            month: "long",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        }
    );
};

const getPaymentType = (
    payment
) =>
    safeText(
        payment?.payment_type_display ||
            payment?.payment_type
    );

const getProviderOrBank = (
    payment
) => {
    if (
        payment?.mobile_money_provider_display
    ) {
        return safeText(
            payment.mobile_money_provider_display
        );
    }

    if (
        payment?.mobile_money_provider
    ) {
        return safeText(
            payment.mobile_money_provider
        );
    }

    if (payment?.bank_display) {
        return safeText(
            payment.bank_display
        );
    }

    if (payment?.bank) {
        return safeText(
            payment.bank
        );
    }

    return "—";
};

const getStatus = (
    payment
) =>
    String(
        payment?.status || ""
    )
        .trim()
        .toLowerCase();

const isPaymentVerified = (
    payment
) =>
    getStatus(payment) ===
        "verified" ||
    payment?.is_verified === true;

const getErrorMessage = (
    error,
    fallback
) => {
    const data =
        error?.response?.data;

    if (
        typeof data === "string"
    ) {
        return data;
    }

    if (data?.detail) {
        return safeText(
            data.detail,
            fallback
        );
    }

    if (data?.message) {
        return safeText(
            data.message,
            fallback
        );
    }

    return fallback;
};

const getReferenceNumber = (
    payment,
    applicationNumber
) =>
    safeText(
        payment?.receipt_number ||
            payment?.receipt_no ||
            payment?.transaction_reference ||
            payment?.reference ||
            applicationNumber,
        applicationNumber
    );

const EntranceReceipt = () => {
    const navigate =
        useNavigate();

    const location =
        useLocation();

    const receiptRef =
        useRef(null);

    const routeState =
        location.state || {};

    const storedReceipt =
        useMemo(
            () =>
                readJSON(
                    RECEIPT_KEY
                ),
            []
        );

    const storedApplication =
        useMemo(
            () =>
                readJSON(
                    APPLICATION_KEY
                ),
            []
        );

    const initialApplicationNumber =
        normalizeApplicationNumber(
            routeState.applicationNumber ||
                storedReceipt?.applicationNumber ||
                storedApplication?.application_number ||
                localStorage.getItem(
                    APPLICATION_NUMBER_KEY
                )
        );

    const [application, setApplication] =
        useState(
            routeState.application ||
                storedReceipt?.application ||
                storedApplication ||
                null
        );

    const [payment, setPayment] =
        useState(
            routeState.payment ||
                storedReceipt?.payment ||
                null
        );

    const [
        applicationNumber,
    ] = useState(
        initialApplicationNumber
    );

    const [loading, setLoading] =
        useState(
            !routeState.payment &&
                !storedReceipt?.payment
        );

    const [error, setError] =
        useState("");

    const [downloading, setDownloading] =
        useState(false);

    const [
        logoLoaded,
        setLogoLoaded,
    ] = useState(false);

    const receiptAmount =
        routeState.amount ||
        storedReceipt?.amount ||
        payment?.amount ||
        850;

    const receiptCurrency =
        routeState.currency ||
        storedReceipt?.currency ||
        payment?.currency ||
        "LRD";

    useEffect(() => {
        if (
            !applicationNumber
        ) {
            setLoading(false);

            setError(
                "No entrance application number was found."
            );

            return;
        }

        if (
            payment &&
            isPaymentVerified(
                payment
            )
        ) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        const loadReceiptData =
            async () => {
                try {
                    setLoading(true);
                    setError("");

                    const response =
                        await api.post(
                            STATUS_ENDPOINT,
                            {
                                application_number:
                                    applicationNumber,
                            }
                        );

                    if (
                        cancelled
                    ) {
                        return;
                    }

                    const data =
                        response?.data ||
                        {};

                    if (
                        data.application
                    ) {
                        setApplication(
                            data.application
                        );

                        localStorage.setItem(
                            APPLICATION_KEY,
                            JSON.stringify(
                                data.application
                            )
                        );
                    }

                    if (data.payment) {
                        setPayment(
                            data.payment
                        );

                        const receiptData =
                            {
                                application:
                                    data.application ||
                                    application,

                                payment:
                                    data.payment,

                                applicationNumber,

                                amount:
                                    data.payment
                                        ?.amount ||
                                    850,

                                currency:
                                    data.payment
                                        ?.currency ||
                                    "LRD",
                            };

                        localStorage.setItem(
                            RECEIPT_KEY,
                            JSON.stringify(
                                receiptData
                            )
                        );
                    }
                } catch (
                    requestError
                ) {
                    if (
                        !cancelled
                    ) {
                        console.error(
                            "Receipt data loading failed:",
                            requestError
                        );

                        setError(
                            getErrorMessage(
                                requestError,
                                "We could not retrieve the verified payment information."
                            )
                        );
                    }
                } finally {
                    if (
                        !cancelled
                    ) {
                        setLoading(false);
                    }
                }
            };

        loadReceiptData();

        return () => {
            cancelled = true;
        };
    }, [
        applicationNumber,
    ]);

    useEffect(() => {
        if (
            payment &&
            isPaymentVerified(
                payment
            )
        ) {
            try {
                localStorage.setItem(
                    RECEIPT_KEY,
                    JSON.stringify({
                        application,
                        payment,
                        applicationNumber,
                        amount:
                            payment.amount ||
                            850,
                        currency:
                            payment.currency ||
                            "LRD",
                    })
                );
            } catch (
                storageError
            ) {
                console.error(
                    "Unable to save receipt:",
                    storageError
                );
            }
        }
    }, [
        payment,
        application,
        applicationNumber,
    ]);

    const applicantName =
        getApplicantName(
            application
        );

    const academicYear =
        safeText(
            application?.academic_year
        );

    const program =
        safeText(
            application?.program
        );

    const verified =
        isPaymentVerified(
            payment
        );

    const referenceNumber =
        getReferenceNumber(
            payment,
            applicationNumber
        );

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadPDF =
        async () => {
            if (!receiptRef.current) {
                return;
            }

            if (!verified) {
                await Swal.fire({
                    icon: "warning",
                    title:
                        "Receipt Not Available",
                    text:
                        "The entrance payment must be verified before the receipt can be downloaded.",
                    confirmButtonColor:
                        BRAND.navy,
                });

                return;
            }

            try {
                setDownloading(true);

                await new Promise(
                    (resolve) =>
                        setTimeout(
                            resolve,
                            150
                        )
                );

                const element =
                    receiptRef.current;

                const canvas =
                    await html2canvas(
                        element,
                        {
                            scale: 2,
                            useCORS: true,
                            allowTaint: false,
                            backgroundColor:
                                "#FFFFFF",
                            logging: false,
                            imageTimeout: 15000,
                            scrollX: 0,
                            scrollY: -window.scrollY,
                            windowWidth:
                                element.scrollWidth,
                        }
                    );

                const imageData =
                    canvas.toDataURL(
                        "image/png",
                        1.0
                    );

                const pdf =
                    new jsPDF({
                        orientation:
                            "portrait",
                        unit: "mm",
                        format: "a4",
                        compress: true,
                    });

                const pageWidth =
                    pdf.internal.pageSize.getWidth();

                const pageHeight =
                    pdf.internal.pageSize.getHeight();

                const margin = 8;

                const usableWidth =
                    pageWidth -
                    margin * 2;

                const imageHeight =
                    (canvas.height *
                        usableWidth) /
                    canvas.width;

                let heightLeft =
                    imageHeight;

                let position =
                    margin;

                pdf.addImage(
                    imageData,
                    "PNG",
                    margin,
                    position,
                    usableWidth,
                    imageHeight,
                    undefined,
                    "FAST"
                );

                heightLeft -=
                    pageHeight -
                    margin * 2;

                while (
                    heightLeft > 0
                ) {
                    position =
                        margin -
                        (imageHeight -
                            heightLeft);

                    pdf.addPage();

                    pdf.addImage(
                        imageData,
                        "PNG",
                        margin,
                        position,
                        usableWidth,
                        imageHeight,
                        undefined,
                        "FAST"
                    );

                    heightLeft -=
                        pageHeight -
                        margin * 2;
                }

                const filename =
                    `Entrance_Receipt_${applicationNumber}.pdf`;

                pdf.save(filename);

                await Swal.fire({
                    icon: "success",
                    title:
                        "Receipt Downloaded",
                    text:
                        "Your official entrance fee receipt has been downloaded as a PDF.",
                    confirmButtonText:
                        "Done",
                    confirmButtonColor:
                        BRAND.green,
                });
            } catch (
                downloadError
            ) {
                console.error(
                    "PDF generation failed:",
                    downloadError
                );

                await Swal.fire({
                    icon: "error",
                    title:
                        "Download Failed",
                    text:
                        "We could not generate the PDF receipt. Please try again or use the Print Receipt option.",
                    confirmButtonColor:
                        BRAND.navy,
                });
            } finally {
                setDownloading(false);
            }
        };

    const handleBack = () => {
        navigate(
            REGISTRATION_ROUTE,
            {
                replace: false,
            }
        );
    };

    if (loading) {
        return (
            <div className="per-loading-page">
                <style>{`
                    .per-loading-page {
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #F3F6FA;
                        font-family: Arial, Helvetica, sans-serif;
                        color: #062A4B;
                        padding: 20px;
                    }

                    .per-loading-card {
                        width: min(420px, 100%);
                        padding: 35px 25px;
                        text-align: center;
                        background: #FFFFFF;
                        border: 1px solid #DCE4EE;
                        border-radius: 18px;
                        box-shadow: 0 18px 50px rgba(6,42,75,.08);
                    }

                    .per-spinner {
                        width: 44px;
                        height: 44px;
                        margin: 0 auto 18px;
                        border: 4px solid #E8EEF4;
                        border-top-color: #063A6B;
                        border-radius: 50%;
                        animation: perSpin .8s linear infinite;
                    }

                    @keyframes perSpin {
                        to {
                            transform: rotate(360deg);
                        }
                    }

                    .per-loading-title {
                        margin: 0;
                        font-size: 18px;
                        font-weight: 900;
                    }

                    .per-loading-text {
                        margin: 8px 0 0;
                        color: #687386;
                        font-size: 12px;
                        line-height: 1.6;
                    }
                `}</style>

                <div className="per-loading-card">
                    <div className="per-spinner" />

                    <h1 className="per-loading-title">
                        Preparing Receipt
                    </h1>

                    <p className="per-loading-text">
                        Please wait while we
                        retrieve your verified
                        entrance payment.
                    </p>
                </div>
            </div>
        );
    }

    if (
        error ||
        !applicationNumber ||
        !payment ||
        !verified
    ) {
        return (
            <div className="per-error-page">
                <style>{`
                    .per-error-page {
                        min-height: 100vh;
                        background: linear-gradient(180deg,#F3F6FA,#FFFFFF);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 20px;
                        font-family: Arial, Helvetica, sans-serif;
                    }

                    .per-error-card {
                        width: min(520px, 100%);
                        background: #FFFFFF;
                        border: 1px solid #DCE4EE;
                        border-radius: 18px;
                        padding: 35px;
                        text-align: center;
                        box-shadow: 0 20px 60px rgba(6,42,75,.08);
                    }

                    .per-error-icon {
                        width: 65px;
                        height: 65px;
                        margin: 0 auto 16px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #FFF6DD;
                        color: #C0750D;
                        font-size: 25px;
                    }

                    .per-error-title {
                        margin: 0;
                        color: #062A4B;
                        font-size: 22px;
                        font-weight: 900;
                    }

                    .per-error-text {
                        margin: 10px 0 22px;
                        color: #687386;
                        font-size: 12px;
                        line-height: 1.7;
                    }

                    .per-error-button {
                        border: none;
                        border-radius: 9px;
                        padding: 12px 18px;
                        background: #062A4B;
                        color: #FFFFFF;
                        font-size: 11px;
                        font-weight: 900;
                        cursor: pointer;
                    }
                `}</style>

                <div className="per-error-card">
                    <div className="per-error-icon">
                        <FaShieldAlt />
                    </div>

                    <h1 className="per-error-title">
                        Receipt Unavailable
                    </h1>

                    <p className="per-error-text">
                        {error ||
                            "This receipt can only be displayed after the entrance payment has been verified."}
                    </p>

                    <button
                        type="button"
                        className="per-error-button"
                        onClick={
                            handleBack
                        }
                    >
                        <FaArrowLeft
                            style={{
                                marginRight: 7,
                            }}
                        />
                        Back to Registration
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="per-page">
            <style>{`
                * {
                    box-sizing: border-box;
                }

                body {
                    margin: 0;
                }

                .per-page {
                    min-height: 100vh;
                    background: linear-gradient(
                        180deg,
                        #F1F5F9 0%,
                        #FFFFFF 55%,
                        #F1F5F9 100%
                    );
                    color: ${BRAND.text};
                    font-family: Arial, Helvetica, sans-serif;
                    padding: 25px 16px 45px;
                }

                .per-container {
                    width: 100%;
                    max-width: 930px;
                    margin: 0 auto;
                }

                .per-toolbar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 15px;
                }

                .per-toolbar-left,
                .per-toolbar-right {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .per-toolbar-button {
                    border: 1px solid ${BRAND.border};
                    background: #FFFFFF;
                    color: ${BRAND.navy};
                    border-radius: 8px;
                    padding: 10px 13px;
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    font-size: 10px;
                    font-weight: 900;
                    cursor: pointer;
                    transition: .2s ease;
                }

                .per-toolbar-button:hover {
                    border-color: ${BRAND.blue};
                    background: #F6F9FC;
                    transform: translateY(-1px);
                }

                .per-toolbar-button.primary {
                    border-color: ${BRAND.navy};
                    background: ${BRAND.navy};
                    color: #FFFFFF;
                }

                .per-toolbar-button.download {
                    border-color: ${BRAND.green};
                    color: ${BRAND.green};
                    background: #FFFFFF;
                }

                .per-toolbar-button.download:hover {
                    background: #F2FAEF;
                    border-color: ${BRAND.green};
                }

                .per-toolbar-button:disabled {
                    opacity: .65;
                    cursor: not-allowed;
                    transform: none;
                }

                .per-receipt {
                    position: relative;
                    background: #FFFFFF;
                    border: 1px solid #D7E0E9;
                    box-shadow: 0 25px 70px rgba(6,42,75,.10);
                    overflow: hidden;
                }

                .per-top-strip {
                    height: 7px;
                    background: linear-gradient(
                        90deg,
                        ${BRAND.navy} 0%,
                        ${BRAND.blue} 65%,
                        ${BRAND.gold} 65%,
                        ${BRAND.gold} 100%
                    );
                }

                .per-header {
                    padding: 30px 34px 24px;
                    border-bottom: 1px solid ${BRAND.border};
                }

                .per-header-main {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 25px;
                }

                .per-organization {
                    display: flex;
                    align-items: center;
                    gap: 17px;
                    min-width: 0;
                }

                .per-logo {
                    width: 84px;
                    height: 84px;
                    object-fit: contain;
                    flex-shrink: 0;
                    border-radius: 14px;
                    background: #FFFFFF;
                }

                .per-logo-fallback {
                    width: 84px;
                    height: 84px;
                    flex-shrink: 0;
                    border-radius: 14px;
                    background: linear-gradient(
                        135deg,
                        ${BRAND.navy},
                        ${BRAND.blue}
                    );
                    color: ${BRAND.gold};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 28px;
                }

                .per-org-name {
                    margin: 0;
                    color: ${BRAND.navy};
                    font-size: 22px;
                    line-height: 1.15;
                    font-weight: 950;
                    letter-spacing: .3px;
                }

                .per-motto {
                    margin: 5px 0 9px;
                    color: ${BRAND.orange};
                    font-size: 10px;
                    font-weight: 800;
                    font-style: italic;
                }

                .per-address {
                    max-width: 570px;
                    margin: 0;
                    color: ${BRAND.muted};
                    font-size: 9px;
                    line-height: 1.55;
                }

                .per-phone-row {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 8px 13px;
                    margin-top: 7px;
                    color: ${BRAND.navy};
                    font-size: 9px;
                    font-weight: 800;
                }

                .per-phone {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                }

                .per-phone svg {
                    color: ${BRAND.green};
                }

                .per-receipt-label {
                    min-width: 165px;
                    padding: 14px;
                    border-left: 4px solid ${BRAND.gold};
                    background: #FFFDF3;
                    text-align: right;
                }

                .per-receipt-label-small {
                    display: block;
                    color: #8A6A00;
                    font-size: 8px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }

                .per-receipt-label-title {
                    display: block;
                    margin-top: 4px;
                    color: ${BRAND.navy};
                    font-size: 20px;
                    font-weight: 950;
                }

                .per-receipt-label-number {
                    display: block;
                    margin-top: 5px;
                    color: ${BRAND.blue};
                    font-size: 9px;
                    font-weight: 900;
                    overflow-wrap: anywhere;
                }

                .per-title-area {
                    padding: 25px 34px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                }

                .per-title {
                    margin: 0;
                    color: ${BRAND.navy};
                    font-size: 23px;
                    font-weight: 950;
                }

                .per-subtitle {
                    margin: 5px 0 0;
                    color: ${BRAND.muted};
                    font-size: 10px;
                }

                .per-verified {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 8px 12px;
                    border-radius: 999px;
                    background: #EAF7E5;
                    color: ${BRAND.green};
                    border: 1px solid #CFE6C7;
                    font-size: 9px;
                    font-weight: 950;
                    letter-spacing: .5px;
                    white-space: nowrap;
                }

                .per-content {
                    padding: 0 34px 30px;
                }

                .per-section {
                    margin-top: 17px;
                    border: 1px solid ${BRAND.border};
                    border-radius: 11px;
                    overflow: hidden;
                }

                .per-section-header {
                    padding: 11px 14px;
                    background: #F7F9FB;
                    border-bottom: 1px solid ${BRAND.border};
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    color: ${BRAND.navy};
                    font-size: 10px;
                    font-weight: 950;
                    text-transform: uppercase;
                    letter-spacing: .6px;
                }

                .per-section-header svg {
                    color: ${BRAND.blue};
                }

                .per-info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                }

                .per-info-item {
                    min-width: 0;
                    padding: 13px 14px;
                    border-bottom: 1px solid #EDF1F5;
                }

                .per-info-item:nth-child(odd) {
                    border-right: 1px solid #EDF1F5;
                }

                .per-info-label {
                    display: block;
                    margin-bottom: 5px;
                    color: #7B8796;
                    font-size: 8px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: .5px;
                }

                .per-info-value {
                    color: ${BRAND.navy};
                    font-size: 11px;
                    font-weight: 850;
                    line-height: 1.5;
                    overflow-wrap: anywhere;
                }

                .per-payment-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .per-payment-table th {
                    padding: 11px 13px;
                    background: #F9FAFC;
                    color: #788493;
                    border-bottom: 1px solid ${BRAND.border};
                    text-align: left;
                    font-size: 8px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: .5px;
                }

                .per-payment-table td {
                    padding: 13px;
                    border-bottom: 1px solid #EDF1F5;
                    color: ${BRAND.navy};
                    font-size: 10px;
                    font-weight: 750;
                    vertical-align: top;
                }

                .per-payment-table tr:last-child td {
                    border-bottom: none;
                }

                .per-amount-row td {
                    background: #FFFDF3;
                    font-weight: 950;
                }

                .per-amount {
                    color: ${BRAND.navy};
                    font-size: 17px;
                    font-weight: 950;
                    white-space: nowrap;
                }

                .per-total {
                    display: flex;
                    align-items: stretch;
                    justify-content: flex-end;
                    margin-top: 18px;
                }

                .per-total-box {
                    min-width: 285px;
                    padding: 16px 18px;
                    border: 1px solid #D9E5EF;
                    border-radius: 10px;
                    background: linear-gradient(
                        135deg,
                        #F7FAFC,
                        #FFFFFF
                    );
                }

                .per-total-label {
                    color: #778493;
                    font-size: 8px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: .7px;
                }

                .per-total-value {
                    margin-top: 4px;
                    color: ${BRAND.navy};
                    font-size: 23px;
                    font-weight: 950;
                }

                .per-verification-box {
                    margin-top: 18px;
                    display: grid;
                    grid-template-columns: 1fr auto;
                    gap: 18px;
                    align-items: center;
                    padding: 17px;
                    border: 1px solid #D4E8CE;
                    border-radius: 11px;
                    background: linear-gradient(
                        135deg,
                        #F5FBF2,
                        #FFFFFF
                    );
                }

                .per-verification-main {
                    display: flex;
                    align-items: flex-start;
                    gap: 11px;
                }

                .per-verification-icon {
                    width: 38px;
                    height: 38px;
                    flex-shrink: 0;
                    border-radius: 50%;
                    background: #E4F4DE;
                    color: ${BRAND.green};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                }

                .per-verification-title {
                    margin: 0;
                    color: ${BRAND.green};
                    font-size: 12px;
                    font-weight: 950;
                }

                .per-verification-text {
                    margin: 4px 0 0;
                    color: #63705F;
                    font-size: 9px;
                    line-height: 1.55;
                }

                .per-seal {
                    width: 82px;
                    height: 82px;
                    border: 3px solid ${BRAND.green};
                    border-radius: 50%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: ${BRAND.green};
                    transform: rotate(-8deg);
                    flex-shrink: 0;
                }

                .per-seal strong {
                    font-size: 11px;
                    font-weight: 950;
                    letter-spacing: .5px;
                }

                .per-seal span {
                    margin-top: 2px;
                    font-size: 7px;
                    font-weight: 900;
                    text-transform: uppercase;
                }

                .per-note {
                    margin-top: 18px;
                    padding: 12px 14px;
                    border-radius: 8px;
                    background: #F6F8FA;
                    color: #687386;
                    font-size: 8px;
                    line-height: 1.65;
                    text-align: center;
                }

                .per-footer {
                    padding: 18px 34px 25px;
                    border-top: 1px dashed #C9D4DF;
                    text-align: center;
                }

                .per-footer-brand {
                    color: ${BRAND.navy};
                    font-size: 9px;
                    font-weight: 950;
                    text-transform: uppercase;
                    letter-spacing: .6px;
                }

                .per-footer-text {
                    margin-top: 5px;
                    color: #8A94A3;
                    font-size: 8px;
                    line-height: 1.5;
                }

                .per-secure {
                    margin-top: 8px;
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    color: ${BRAND.green};
                    font-size: 8px;
                    font-weight: 800;
                }

                @media (max-width: 700px) {
                    .per-page {
                        padding: 10px;
                    }

                    .per-header {
                        padding: 22px 18px;
                    }

                    .per-header-main,
                    .per-title-area {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .per-receipt-label {
                        width: 100%;
                        text-align: left;
                    }

                    .per-title-area,
                    .per-content {
                        padding-left: 18px;
                        padding-right: 18px;
                    }

                    .per-info-grid {
                        grid-template-columns: 1fr;
                    }

                    .per-info-item:nth-child(odd) {
                        border-right: none;
                    }

                    .per-payment-table {
                        min-width: 600px;
                    }

                    .per-section.payment {
                        overflow-x: auto;
                    }

                    .per-verification-box {
                        grid-template-columns: 1fr;
                    }

                    .per-seal {
                        margin: 0 auto;
                    }

                    .per-total {
                        justify-content: stretch;
                    }

                    .per-total-box {
                        width: 100%;
                    }

                    .per-toolbar {
                        align-items: stretch;
                        flex-direction: column;
                    }

                    .per-toolbar-left,
                    .per-toolbar-right {
                        width: 100%;
                    }

                    .per-toolbar-button {
                        flex: 1;
                        justify-content: center;
                    }

                    .per-logo,
                    .per-logo-fallback {
                        width: 68px;
                        height: 68px;
                    }

                    .per-org-name {
                        font-size: 18px;
                    }
                }

                @media print {
                    @page {
                        size: A4;
                        margin: 10mm;
                    }

                    body {
                        background: #FFFFFF !important;
                    }

                    .per-page {
                        padding: 0 !important;
                        background: #FFFFFF !important;
                    }

                    .per-toolbar {
                        display: none !important;
                    }

                    .per-container {
                        max-width: none;
                    }

                    .per-receipt {
                        border: none;
                        box-shadow: none;
                    }

                    .per-top-strip {
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }

                    .per-section,
                    .per-verification-box,
                    .per-total-box {
                        break-inside: avoid;
                    }

                    .per-header,
                    .per-content,
                    .per-footer {
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
                }
            `}</style>

            <div className="per-container">
                <div className="per-toolbar">
                    <div className="per-toolbar-left">
                        <button
                            type="button"
                            className="per-toolbar-button"
                            onClick={
                                handleBack
                            }
                        >
                            <FaArrowLeft />
                            Back
                        </button>
                    </div>

                    <div className="per-toolbar-right">
                        <button
                            type="button"
                            className="per-toolbar-button download"
                            onClick={
                                handleDownloadPDF
                            }
                            disabled={
                                downloading
                            }
                        >
                            <FaDownload />

                            {downloading
                                ? "Generating PDF..."
                                : "Download PDF"}
                        </button>

                        <button
                            type="button"
                            className="per-toolbar-button primary"
                            onClick={
                                handlePrint
                            }
                        >
                            <FaPrint />
                            Print Receipt
                        </button>
                    </div>
                </div>

                <article
                    ref={receiptRef}
                    className="per-receipt"
                >
                    <div className="per-top-strip" />

                    <header className="per-header">
                        <div className="per-header-main">
                            <div className="per-organization">
                                {logoLoaded ? (
                                    <img
                                        src={
                                            brandLogo
                                        }
                                        alt={`${ORGANIZATION.name} Logo`}
                                        className="per-logo"
                                    />
                                ) : (
                                    <>
                                        <img
                                            src={
                                                brandLogo
                                            }
                                            alt={`${ORGANIZATION.name} Logo`}
                                            className="per-logo"
                                            onLoad={() =>
                                                setLogoLoaded(
                                                    true
                                                )
                                            }
                                            onError={() =>
                                                setLogoLoaded(
                                                    false
                                                )
                                            }
                                        />

                                        {!brandLogo && (
                                            <div className="per-logo-fallback">
                                                <FaReceipt />
                                            </div>
                                        )}
                                    </>
                                )}

                                <div>
                                    <h1 className="per-org-name">
                                        {
                                            ORGANIZATION.name
                                        }
                                    </h1>

                                    <p className="per-motto">
                                        "
                                        {
                                            ORGANIZATION.motto
                                        }
                                        "
                                    </p>

                                    <p className="per-address">
                                        {
                                            ORGANIZATION.address
                                        }
                                    </p>

                                    <div className="per-phone-row">
                                        {ORGANIZATION.phones.map(
                                            (
                                                phone
                                            ) => (
                                                <span
                                                    className="per-phone"
                                                    key={
                                                        phone
                                                    }
                                                >
                                                    <FaPhoneAlt />
                                                    {
                                                        phone
                                                    }
                                                </span>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="per-receipt-label">
                                <span className="per-receipt-label-small">
                                    Official Document
                                </span>

                                <span className="per-receipt-label-title">
                                    RECEIPT
                                </span>

                                <span className="per-receipt-label-number">
                                    {
                                        applicationNumber
                                    }
                                </span>
                            </div>
                        </div>
                    </header>

                    <div className="per-title-area">
                        <div>
                            <h2 className="per-title">
                                Entrance Fee Receipt
                            </h2>

                            <p className="per-subtitle">
                                Official acknowledgement
                                of verified entrance
                                application payment
                            </p>
                        </div>

                        <div className="per-verified">
                            <FaCheckCircle />
                            PAYMENT VERIFIED
                        </div>
                    </div>

                    <div className="per-content">
                        <section className="per-section">
                            <div className="per-section-header">
                                <FaUserGraduate />
                                Applicant Information
                            </div>

                            <div className="per-info-grid">
                                <ReceiptInfo
                                    label="Applicant Name"
                                    value={
                                        applicantName
                                    }
                                />

                                <ReceiptInfo
                                    label="Application Number"
                                    value={
                                        applicationNumber
                                    }
                                />

                                <ReceiptInfo
                                    label="Program"
                                    value={
                                        program
                                    }
                                />

                                <ReceiptInfo
                                    label="Academic Year"
                                    value={
                                        academicYear
                                    }
                                />

                                <ReceiptInfo
                                    label="Receipt Reference"
                                    value={
                                        referenceNumber
                                    }
                                />

                                <ReceiptInfo
                                    label="Receipt Date"
                                    value={formatDate(
                                        payment?.verified_date ||
                                            payment?.verified_at ||
                                            payment?.payment_date
                                    )}
                                />
                            </div>
                        </section>

                        <section className="per-section payment">
                            <div className="per-section-header">
                                <FaFileInvoiceDollar />
                                Payment Information
                            </div>

                            <table className="per-payment-table">
                                <thead>
                                    <tr>
                                        <th>
                                            Description
                                        </th>

                                        <th>
                                            Payment Method
                                        </th>

                                        <th>
                                            Reference
                                        </th>

                                        <th>
                                            Date
                                        </th>

                                        <th>
                                            Amount
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr>
                                        <td>
                                            Entrance
                                            Application
                                            Fee
                                        </td>

                                        <td>
                                            {getPaymentType(
                                                payment
                                            )}

                                            {getProviderOrBank(
                                                payment
                                            ) !==
                                                "—" && (
                                                <div
                                                    style={{
                                                        marginTop:
                                                            4,
                                                        color:
                                                            BRAND.muted,
                                                        fontSize:
                                                            8,
                                                    }}
                                                >
                                                    {
                                                        getProviderOrBank(
                                                            payment
                                                        )
                                                    }
                                                </div>
                                            )}
                                        </td>

                                        <td>
                                            {safeText(
                                                payment?.transaction_reference ||
                                                    payment?.reference
                                            )}
                                        </td>

                                        <td>
                                            {formatDate(
                                                payment?.payment_date
                                            )}
                                        </td>

                                        <td className="per-amount">
                                            {formatMoney(
                                                receiptAmount,
                                                receiptCurrency
                                            )}
                                        </td>
                                    </tr>

                                    <tr className="per-amount-row">
                                        <td
                                            colSpan={
                                                4
                                            }
                                        >
                                            Total Paid
                                        </td>

                                        <td className="per-amount">
                                            {formatMoney(
                                                receiptAmount,
                                                receiptCurrency
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </section>

                        <div className="per-total">
                            <div className="per-total-box">
                                <div className="per-total-label">
                                    Amount Received
                                </div>

                                <div className="per-total-value">
                                    {formatMoney(
                                        receiptAmount,
                                        receiptCurrency
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="per-verification-box">
                            <div className="per-verification-main">
                                <div className="per-verification-icon">
                                    <FaCheckCircle />
                                </div>

                                <div>
                                    <h3 className="per-verification-title">
                                        Payment Successfully
                                        Verified
                                    </h3>

                                    <p className="per-verification-text">
                                        This receipt confirms
                                        that the entrance
                                        application payment
                                        associated with
                                        application{" "}
                                        <strong>
                                            {
                                                applicationNumber
                                            }
                                        </strong>{" "}
                                        has been reviewed and
                                        verified by the
                                        institution.
                                    </p>

                                    <p
                                        className="per-verification-text"
                                        style={{
                                            marginTop:
                                                5,
                                        }}
                                    >
                                        Verification Date:{" "}
                                        <strong>
                                            {formatDate(
                                                payment?.verified_date ||
                                                    payment?.verified_at
                                            )}
                                        </strong>
                                    </p>
                                </div>
                            </div>

                            <div className="per-seal">
                                <FaCheckCircle
                                    style={{
                                        fontSize: 17,
                                        marginBottom: 2,
                                    }}
                                />

                                <strong>
                                    VERIFIED
                                </strong>

                                <span>
                                    Official
                                </span>
                            </div>
                        </div>

                        <div className="per-note">
                            This receipt is generated from
                            the verified entrance payment
                            record. Please retain it for
                            your records and present it to
                            the institution when requested.
                            Any alteration to this document
                            may invalidate the receipt.
                        </div>
                    </div>

                    <footer className="per-footer">
                        <div className="per-footer-brand">
                            {
                                ORGANIZATION.name
                            }
                        </div>

                        <div className="per-footer-text">
                            Entrance Registration &
                            Payment Services
                            <br />
                            {
                                ORGANIZATION.address
                            }
                            <br />
                            Tel:{" "}
                            {
                                ORGANIZATION.phones.join(
                                    " / "
                                )
                            }
                        </div>

                        <div className="per-secure">
                            <FaLock />
                            Secure Verified Payment
                            Receipt
                        </div>
                    </footer>
                </article>
            </div>
        </div>
    );
};

const ReceiptInfo = ({
    label,
    value,
}) => (
    <div className="per-info-item">
        <span className="per-info-label">
            {label}
        </span>

        <div className="per-info-value">
            {safeText(value)}
        </div>
    </div>
);

export default EntranceReceipt;